import { readCreatureStatuses, storedStatusRows } from './creature-status-reader.js';
import { readItemField } from './item-field-reader.js';
import { createIntegerRollupCompiler } from './integer-layer-rollup.js';
import { createAbilityFinalizerGates } from './ability-finalizer-gates.js';
import { createFormulaReadContext } from './formula-read-context.js';
import { statusProjectionDependencies } from './status-attack-budget.js';
import { statusRuntimeLayerKeys } from '../statuses/runtime-layers.js';
import { compileCreatureSpeeds } from './creature-speeds.js';
import { isTimedPassiveSpeedLayer } from './combat-start-timed-passive.js';
import { createStatusLayerReadContext } from './status-row-construction.js';
import { decisionWrites } from '../movement/decision-view.js';
import { STATUS_RESIDUAL_LAYERS } from './status-residue-layers.js';
import { compileStatusResidueFields, applyStatusRootFields, type StatusRootFields } from './status-root-residue.js';
import { layerReadView } from './layer-read-view.js';

type Row = Record<string, unknown>;
const record = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const upper = (v: unknown): string => String(v ?? '').toUpperCase();
const LIGHT = 'bright_light_dim_light';
export type StatusRemovalRuntime = { ok: true; light?: { value: unknown; sources: unknown }; suppressed?: Row; speeds?: Row; residue?: StatusRootFields }
    | { ok: false; layer: string };

/** Re-evaluate only the clusters whose sources disappear with a supported status.
 * No wire extension: sender, subscriber and disposable round state derive from their held rows. */
export function statusRemovalRuntime(row: Row, statusIds: readonly string[], definition: (id: string) => Row | null): StatusRemovalRuntime {
    return statusRuntime(row, statusIds, definition, true);
}

/** Additions already occupy the held status bucket. Only their timed speed
 * footprint needs this projection; other additions carry their own decisions. */
export function applyTimedPassiveSpeedAddition(row: Row, statusIds: readonly string[], definition: (id: string) => Row | null): void {
    const runtime = statusRuntime(row, statusIds, definition, false);
    if (!runtime.ok) throw new Error('status-compiled-footprint:' + runtime.layer);
    applyStatusRemovalRuntime(row, runtime);
}

function statusRuntime(row: Row, statusIds: readonly string[], definition: (id: string) => Row | null, removal: boolean): StatusRemovalRuntime {
    if (!statusIds.length) return { ok: true };
    const changed = new Set(statusIds.map(upper));
    const affected = storedStatusRows(row, 'target').filter(s => changed.has(upper(readItemField(s, 'status_id')))).map(s =>
        ({ ...s, status_arr: record(s.status_arr) ?? definition(upper(readItemField(s, 'status_item_id'))) }));
    const has = (s: Row, key: string): boolean => statusRuntimeLayerKeys(record(s.status_arr)).includes(key);
    const light = removal && affected.some(s => has(s, LIGHT)), override = removal && affected.some(s => has(s, 'override_ability'));
    const deps = statusProjectionDependencies(row, definition);
    const readers = createStatusLayerReadContext(deps);
    const speed = affected.some(s => statusRuntimeLayerKeys(record(s.status_arr))
        .some(key => isTimedPassiveSpeedLayer(s.status_arr as Row, key, readers.layerValue)));
    const residueLayers = removal ? [...new Set(affected.flatMap(s => statusRuntimeLayerKeys(record(s.status_arr))))]
        .filter(key => STATUS_RESIDUAL_LAYERS.includes(key)) : [];
    if (!light && !override && !speed && !residueLayers.length) return { ok: true };
    const statuses = Object.values(readCreatureStatuses(row, 'target', definition)) as Row[];
    const target = removal ? statuses.filter(s => !changed.has(upper(readItemField(s, 'status_id')))) : statuses;
    const host = layerReadView(row, { statuses: { ...record(row.statuses), target } });
    let layer = residueLayers[0] ?? LIGHT;
    try {
        const result: Extract<StatusRemovalRuntime, { ok: true }> = { ok: true };
        if (residueLayers.length) result.residue = compileStatusResidueFields(row, host, {
            definition, entries: (source, key) => Object.values(readers.layerEntries(source, key)), value: readers.layerValue,
            quantityIds: deps.quantityIds }, residueLayers);
        if (speed) {
            layer = 'timed-passive-speed';
            const compiled = compileCreatureSpeeds(host, deps);
            if (!compiled.ok) return { ok: false, layer: compiled.reason };
            result.speeds = compiled.speeds;
        }
        if (light) {
            layer = LIGHT;
            const compiled = createIntegerRollupCompiler({ ...deps, metadata: { ...deps.metadata, integerLayers: [LIGHT] } }).compile(host) as Row;
            const creature = record(record(compiled.computed)?.creature);
            result.light = { value: record(creature?.modifiers)?.[LIGHT], sources: record(creature?.modifier_sources)?.[LIGHT] };
        }
        if (override) {
            layer = 'override_ability';
            // Rebuild the suppression set from retained native providers, then add retained status providers
            // to a disposable passive bucket. The native override reader owns ids, nesting and conditions.
            const computed = { ...record((host as Row).computed) }, creature = { ...record(computed.creature) };
            const unsuppressed: Row = { ...host, computed: { ...computed, creature } };
            delete creature.suppressed_ability_ids;
            const readers = createFormulaReadContext(deps);
            // The held ability buckets already contain eligible feat/class grants. Feed those
            // curated providers to the override reader without re-pruning unrelated raw feats.
            const abilities = Object.fromEntries(deps.abilityTypes.map(type => [type, Object.values(readers.layerEntries(unsuppressed, type))]));
            abilities.passive_ability = [...(abilities.passive_ability ?? []),
                ...target.filter(s => has(s, 'override_ability')).map(s => ({ item_arr: s.status_arr }))];
            const view = { computed: { creature: { abilities } } };
            const native = createAbilityFinalizerGates({ ...deps, defenseIntegerLayers: [] });
            const suppression = (input: Row): Row => {
                const compiled = native.override(input) as Row;
                return record(record(record(compiled.computed)?.creature)?.suppressed_ability_ids) ?? {};
            };
            const retained = suppression(view);
            const lost = suppression({ computed: { creature: { abilities: {
                passive_ability: affected.filter(s => has(s, 'override_ability')).map(s => ({ item_arr: s.status_arr })),
            } } } });
            // Only the removed providers' ids change. Other banked suppressions may originate
            // in already-pruned skill grants, and are outside this status-removal decision.
            result.suppressed = { ...record(record(record(row.computed)?.creature)?.suppressed_ability_ids) };
            for (const id of Object.keys(lost)) {
                if (retained[id]) result.suppressed[id] = retained[id]; else delete result.suppressed[id];
            }
        }
        return result;
    } catch { return { ok: false, layer }; }
}

export function applyStatusRemovalRuntime(row: Row, runtime: Extract<StatusRemovalRuntime, { ok: true }>): void {
    applyStatusRootFields(row, runtime.residue);
    if (!runtime.light && !runtime.suppressed && !runtime.speeds) return;
    const computed = (row.computed ??= {}) as Row, creature = (computed.creature ??= {}) as Row;
    if (runtime.speeds) {
        const speeds = (creature.speeds ??= {}) as Row;
        for (const key of new Set([...Object.keys(speeds), ...Object.keys(runtime.speeds)])) {
            if (JSON.stringify(speeds[key]) === JSON.stringify(runtime.speeds[key])) continue;
            if (runtime.speeds[key] === undefined) delete speeds[key]; else speeds[key] = runtime.speeds[key];
        }
    }
    if (runtime.light) for (const [key, value] of [['modifiers', runtime.light.value], ['modifier_sources', runtime.light.sources]] as const) {
        const cluster = (creature[key] ??= {}) as Row;
        if (value === undefined) delete cluster[LIGHT]; else cluster[LIGHT] = structuredClone(decisionWrites(value)); // dnr-clone: allow size=one changed light modifier or its source list; why=the installed leaf owns its mutable output
    }
    if (runtime.suppressed) {
        if (Object.keys(runtime.suppressed).length) creature.suppressed_ability_ids = structuredClone(decisionWrites(runtime.suppressed));
        else delete creature.suppressed_ability_ids;
    }
}
